import React from 'react';
import NewPopupLayout from '../common/NewPopupLayout';

interface PainScalePopupProps {
  onCloseIconClicked: () => void;
  isOpen: boolean;
}

const painColorScale = [
  {
    level: 'Very Tight',
    description: 'Significant restriction, difficult to move into position',
    color: '#FF0004'
  },
  { level: 'Tight', description: 'Noticeable restriction, some discomfort', color: '#FF8184' },
  { level: 'Moderate', description: 'Average mobility, manageable range', color: '#F3A742' },
  {
    level: 'Flexible',
    description: 'Good range of motion, little to no restriction',
    color: '#2AD76F'
  },
  {
    level: 'Very Flexible',
    description: 'Exceptional range, moves easily into full position',
    color: '#2AD76F'
  }
];

const flexibilityScale = [
  {
    range: 'Very Low Flexibility (0-20)',
    description: 'Severe stiffness, limited range of motion',
    bg: '#FFE2E2',
    border: '#ECD6D6',
    rangeColor: '#CB3737',
    descColor: '#E47171'
  },
  {
    range: 'Low Flexibility (21-40)',
    description: 'Tight muscles, needs consistent stretching',
    bg: '#FFF4E6',
    border: '#EFDABD',
    rangeColor: '#D7742D',
    descColor: 'rgba(215, 116, 45, 0.60)'
  },
  {
    range: 'Moderate Flexibility (41-60)',
    description: 'Average mobility, some tight areas',
    bg: '#FFF5CF',
    border: '#D6CCAC',
    rangeColor: '#713F12',
    descColor: 'rgba(113, 63, 18, 0.60)'
  },
  {
    range: 'Good Flexibility (61-80)',
    description: 'Good range of motion, healthy mobility',
    bg: '#EAFFE6',
    border: '#CFE0CB',
    rangeColor: '#32B66F',
    descColor: 'rgba(50, 182, 111, 0.70)'
  },
  {
    range: 'Excellent Flexibility (81-100)',
    description: 'Highly flexible, advanced mobility',
    bg: '#EBF7EC',
    border: '#C0DCC2',
    rangeColor: '#04471B',
    descColor: 'rgba(4, 71, 27, 0.60)'
  }
];

const PainScalePopup = ({ onCloseIconClicked, isOpen }: PainScalePopupProps) => {
  return (
    <NewPopupLayout onCloseIconClicked={onCloseIconClicked}>
      <div className="flex flex-col gap-[20px] pb-[45px] md:pb-0 overflow-y-auto max-h-[calc(90dvh-90px)] md:max-h-[calc(100vh-320px)] md:overflow-y-auto">
        <h2 className="text-[17px] md:text-[19px] leading-[22px] font-Inter font-semibold text-[#333]">
          Understanding our colour scales.
        </h2>

        {/* Pain colour scales */}
        <div className="flex flex-col gap-[10px]">
          <p className="text-[16px] leading-[22px] font-Inter font-semibold text-[#757575] m-0">
            Pain colour scales.
          </p>
          <div className="flex flex-col gap-[20px] p-[20px] rounded-[10px] border border-[#EBEBEB] shadow-[0px_2px_4px_0px_rgba(188,188,188,0.12)]">
            {painColorScale.map((item, index) => (
              <div key={index} className="flex items-center gap-[15px]">
                <div
                  className="shrink-0 w-[30px] h-[30px] rounded-[12px] opacity-70"
                  style={{ backgroundColor: item.color, border: `0.6px solid ${item.color}` }}
                />
                <div className="flex flex-col">
                  <p className="text-[14px] leading-[20px] font-Inter font-medium text-[#000] m-0">
                    {item.level}
                  </p>
                  <p className="text-[13px] leading-[20px] font-Inter font-medium text-[#929292] m-0">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Flexibility Score Scale */}
        <div className="flex flex-col gap-[10px]">
          <p className="text-[16px] leading-[22px] font-Inter font-semibold text-[#757575] m-0">
            Flexibility Score Scale
          </p>
          <div className="flex flex-col gap-[20px] p-[20px] rounded-[10px] border border-[#F0F0F0] shadow-[0px_2px_4px_0px_rgba(188,188,188,0.12)]">
            {flexibilityScale.map((item, index) => (
              <div
                key={index}
                className="w-full rounded-[9px] pt-[15px] pb-[13px] pl-[18px] pr-[60px]"
                style={{ backgroundColor: item.bg, border: `1px solid ${item.border}` }}
              >
                <p
                  className="text-[11.5px] leading-[20px] font-Inter font-medium m-0"
                  style={{ color: item.rangeColor }}
                >
                  {item.range}
                </p>
                <p
                  className="text-[11.5px] leading-[20px] font-Inter font-medium m-0"
                  style={{ color: item.descColor }}
                >
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </NewPopupLayout>
  );
};

export default PainScalePopup;
